import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Button, Dialog, DialogActions, DialogContent, FormControl, DialogContentText, DialogTitle, FormControlLabel, FormHelperText, Switch, IconButton, Accordion, AccordionSummary, Typography, AccordionDetails } from '@mui/material';
import { Close as CloseIcon, ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useCookieConsentContext } from '@use-cookie-consent/react';
import propTypes from 'prop-types';
import { closeCookieBanner as closeCookieBannerAction, closeCookiePreferences as closeCookiePreferencesAction } from '../../../features/layoutSlice';
import { cookieStateAdapter } from '../../../utils/cookieUtils';
import config from '../../../app/config';

function CookiePreferences({ handleCookieSubmit }) {
  const { consent } = useCookieConsentContext();
  const [cookieLocalState, setCookieLocalState] = React.useState(cookieStateAdapter(consent, config.cookies.supportedCookies));
  const [accordionExpanded, setAccordionExpanded] = React.useState([]);
  const isOpen = useSelector((state) => state.layout.isCookiePreferencesOpen);
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const close = () => {
    dispatch(closeCookiePreferencesAction());
  };
  const closeCookieBanner = () => {
    dispatch(closeCookieBannerAction());
  };
  const handleSwitchChange = (event) => {
    const { name, checked } = event.target;
    setCookieLocalState({ ...cookieLocalState, [name]: checked });
  };
  const handleSubmit = () => {
    handleCookieSubmit(cookieLocalState);
    closeCookieBanner();
    close();
  };
  const handleAcceptAll = () => {
    setCookieLocalState(
      config.cookies.supportedCookies.reduce((acc, cookie) => {
        acc[cookie] = true;
        return acc;
      }, {})
    );
  };
  const handleRejectAll = () => {
    setCookieLocalState(
      config.cookies.supportedCookies.reduce((acc, cookie) => {
        if (cookie === 'necessary') {
          acc[cookie] = true;
        } else {
          acc[cookie] = false;
        }
        return acc;
      }, {})
    );
  };
  const resetPreferences = () => {
    setCookieLocalState(cookieStateAdapter(consent, config.cookies.supportedCookies));
  };
  const handleCloseClick = () => {
    resetPreferences();
    close();
  };
  const handleAccordionToggle = (panel) => (event) => {
    setAccordionExpanded((prevAccordionExpanded) => {
      if (prevAccordionExpanded.includes(panel)) {
        return prevAccordionExpanded.filter((p) => p !== panel);
      }
      return [...prevAccordionExpanded, panel];
    });
  };

  return (
    <Dialog variant="cookie_preferences" open={!!isOpen} aria-labelledby="CookiePreferences-title" aria-describedby="CookiePreferences-description" data-testid="cookiePreferences" scroll="paper">
      <IconButton aria-label="close" onClick={handleCloseClick} sx={{ position: 'absolute', right: 8, top: 8 }}>
        <CloseIcon />
      </IconButton>
      <DialogTitle id="CookiePreferences-title">{t('cookies.preferencesDialog.heading')}</DialogTitle>
      <DialogContent>
        <DialogContentText id="CookiePreferences-description">
          {t('cookies.preferencesDialog.description')} <Link to="privacy/cookie">{t('cookies.link')}</Link>
        </DialogContentText>
        <FormControl component="fieldset" variant="standard">
          <Accordion square elevation={0} expanded={accordionExpanded.includes('Necessary-panel')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon onClick={handleAccordionToggle('Necessary-panel')} />} aria-controls="Necessary-content" id="Necessary-header" sx={{ paddingLeft: 4 }}>
              <Typography>{t(`cookies.categories.necessary.label`)}</Typography>
              <FormHelperText sx={{ position: 'absolute', right: 50, top: '50%', transform: 'translateY(-50%)' }}>{t(`cookies.categories.necessary.helperText`)}</FormHelperText>
            </AccordionSummary>
            <AccordionDetails>
              <Typography>{t(`cookies.categories.necessary.description`)}</Typography>
            </AccordionDetails>
          </Accordion>
          {Object.keys(cookieLocalState).map((cookieType) =>
            cookieType !== 'necessary' ? (
              <Accordion key={cookieType} square elevation={0} expanded={accordionExpanded.includes(`${cookieType}-panel`)}>
                <AccordionSummary expandIcon={<ExpandMoreIcon onClick={handleAccordionToggle(`${cookieType}-panel`)} />} aria-controls={`${cookieType}-content`} id={`${cookieType}-header`}>
                  <FormControlLabel control={<Switch checked={cookieLocalState[cookieType]} onChange={handleSwitchChange} name={cookieType} />} label={t(`cookies.categories.${cookieType}.label`)} labelPlacement="start" />
                  <FormHelperText>{t(`cookies.categories.${cookieType}.helperText`)}</FormHelperText>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography>{t(`cookies.categories.${cookieType}.description`)}</Typography>
                </AccordionDetails>
              </Accordion>
            ) : null
          )}
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleAcceptAll} data-testid="acceptAll">
          {t('cookies.actions.acceptAll')}
        </Button>
        <Button onClick={handleRejectAll} data-testid="rejectAll">
          {t('cookies.actions.rejectAll')}
        </Button>
        <Button variant="contained" onClick={handleSubmit}>
          {t('cookies.actions.submit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CookiePreferences.propTypes = {
  handleCookieSubmit: propTypes.func.isRequired,
};

export default CookiePreferences;
